/* eslint-disable id-length */

import { memo } from "react";
import { type EdgeProps, BaseEdge, Edge, XYPosition } from "@xyflow/react";

export type ElkSmoothStepEdgeType = Edge & {
  pathOptions: {
    points: XYPosition[];
  };
};

function createElkSmoothStepEdge() {
  return memo(({ pathOptions, style, markerEnd }: EdgeProps) => {
    const borderRadius = 12;
    const points = pathOptions?.points || [];
    const path = getElkSmoothStepPath({ points, borderRadius });

    return <BaseEdge markerEnd={markerEnd} path={path} style={style} />;
  });
}

const ElkSmoothStepEdge = createElkSmoothStepEdge();
export default ElkSmoothStepEdge;

/**
 * This is a port of the `getSmoothStepPath` function from reactflow
 * modified to use multiple bend points from ELK.
 */
export function getElkSmoothStepPath({
  points,
  borderRadius,
}: {
  points: XYPosition[];
  borderRadius: number;
}) {
  return points.reduce<string>((res, p, i) => {
    let segment = "";

    if (i > 0 && i < points.length - 1) {
      segment = getBend(points[i - 1], p, points[i + 1], borderRadius);
    } else {
      segment = `${i === 0 ? "M" : "L"}${p.x} ${p.y}`;
    }

    res += segment;

    return res;
  }, "");
}

// This is ported directly from the getSmoothStepPath function from reactflow
// @see https://github.com/xyflow/xyflow/blob/dcddc7053539aac930c5ea077e8f388f9dcd2f34/packages/system/src/utils/edges/smoothstep-edge.ts
const distance = (a: XYPosition, b: XYPosition) =>
  Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));

// This is ported directly from the getSmoothStepPath function from reactflow
// @see https://github.com/xyflow/xyflow/blob/dcddc7053539aac930c5ea077e8f388f9dcd2f34/packages/system/src/utils/edges/smoothstep-edge.ts
function getBend(a: XYPosition, b: XYPosition, c: XYPosition, size: number): string {
  const bendSize = Math.min(distance(a, b) / 2, distance(b, c) / 2, size);
  const { x, y } = b;

  // no bend
  if ((a.x === x && x === c.x) || (a.y === y && y === c.y)) {
    return `L${x} ${y}`;
  }

  // first segment is horizontal
  if (a.y === y) {
    const xDir = a.x < c.x ? -1 : 1;
    const yDir = a.y < c.y ? 1 : -1;
    return `L ${x + bendSize * xDir},${y}Q ${x},${y} ${x},${y + bendSize * yDir}`;
  }

  const xDir = a.x < c.x ? 1 : -1;
  const yDir = a.y < c.y ? -1 : 1;
  return `L ${x},${y + bendSize * yDir}Q ${x},${y} ${x + bendSize * xDir},${y}`;
}
